import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { 
  ChefHat, 
  Building2, 
  Upload, 
  Sparkles, 
  ArrowRight, 
  ArrowLeft,
  Loader2,
  Check,
  Image as ImageIcon,
  MapPin
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import MenuImportDialog from "../components/MenuImportDialog";
import { businessTypes, cuisineTypes, priceRanges } from "../lib/utils";

const steps = [
  { id: 1, title: "Tipo de negocio", description: "Cuéntanos sobre tu establecimiento" },
  { id: 2, title: "Importar o crear", description: "Elige cómo empezar" },
  { id: 3, title: "Configuración", description: "Últimos detalles" }
];

export default function OnboardingPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [importMethod, setImportMethod] = useState(null); // 'import' | 'create' | 'ai'
  
  const [formData, setFormData] = useState({
    name: "",
    business_type: "",
    cuisine_type: "",
    price_range: "medium",
    target_audience: "",
    address: "",
    phone: "",
    description: "",
    ui_mode: "simple", // Modo por defecto: Simple (para no-técnicos)
  });

  const [importData, setImportData] = useState({
    menuImage: null,
    menuText: ""
  });

  const [aiGeneratedMenu, setAiGeneratedMenu] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importedMenuData, setImportedMenuData] = useState(null); // { import_id, items, total_food, total_beverages, menu_name }
  const [geoCoords, setGeoCoords] = useState(null); // {lat, lng}
  const [locating, setLocating] = useState(false);

  const handleUseMyLocation = async () => {
    if (!navigator.geolocation) {
      toast.error("Tu navegador no soporta geolocalización");
      return;
    }
    setLocating(true);
    try {
      const pos = await new Promise((resolve, reject) =>
        navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 10000 })
      );
      const { latitude: lat, longitude: lng } = pos.coords;
      setGeoCoords({ lat, lng });
      // Reverse geocode via backend proxy (Photon primary + Nominatim fallback, CORS-safe)
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/geo/reverse?lat=${lat}&lng=${lng}`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        const pretty = j?.pretty || j?.display_name || "";
        if (pretty) {
          setFormData(prev => ({ ...prev, address: pretty }));
          toast.success("Ubicación detectada", { description: pretty });
        } else {
          toast.success("Ubicación detectada", { description: `${lat.toFixed(4)}, ${lng.toFixed(4)}` });
        }
      } catch {
        toast.success("Ubicación detectada", { description: `${lat.toFixed(4)}, ${lng.toFixed(4)}` });
      }
    } catch (e) {
      toast.error(e?.message || "No se pudo obtener tu ubicación (permisos denegados)");
    } finally {
      setLocating(false);
    }
  };

  const openImportDialog = () => setImportDialogOpen(true);

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target.result.split(",")[1];
        setImportData({ ...importData, menuImage: base64 });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImportMenu = async () => {
    if (!importData.menuImage && !importData.menuText) {
      toast.error("Sube una imagen o introduce el texto del menú");
      return;
    }

    setAiLoading(true);
    try {
      const result = await api.aiImportMenu({
        image_base64: importData.menuImage,
        text_content: importData.menuText
      });
      setAiGeneratedMenu(result);
      toast.success("¡Menú importado con éxito!");
      setCurrentStep(3);
    } catch (error) {
      toast.error("Error al importar el menú");
    } finally {
      setAiLoading(false);
    }
  };

  const handleGenerateMenu = async () => {
    if (!formData.business_type || !formData.cuisine_type) {
      toast.error("Selecciona tipo de negocio y cocina");
      return;
    }

    setAiLoading(true);
    try {
      const result = await api.aiGenerateMenu({
        business_type: formData.business_type,
        cuisine_type: formData.cuisine_type,
        price_range: formData.price_range,
        target_audience: formData.target_audience
      });
      setAiGeneratedMenu(result);
      toast.success("¡Menú generado con IA!");
      setCurrentStep(3);
    } catch (error) {
      toast.error("Error al generar el menú");
    } finally {
      setAiLoading(false);
    }
  };

  const handleFinish = async () => {
    if (!formData.name) {
      toast.error("Introduce el nombre de tu negocio");
      return;
    }

    setLoading(true);
    try {
      // iter.256 — mapea importMethod → default_landing (Fase 11 delight).
      // import/ai → user probablemente quiere gestionar la carta → landing "menus"
      // create   → sin foco previo → dashboard default
      const LANDING_MAP = { import: "menus", ai: "menus", create: "dashboard" };
      const default_landing = LANDING_MAP[importMethod] || "dashboard";

      // Persist UI mode preference (Fase 7 — Modo Simple/Avanzado)
      // + default_landing (Fase 11 · iter.256). Fire-and-forget.
      api.request("/auth/me/preferences", {
        method: "PATCH",
        body: JSON.stringify({ ui_mode: formData.ui_mode || "simple", default_landing }),
      }).catch(() => {});

      // iter.247 · Fase 9 — telemetría de elección de UI mode en onboarding.
      // Fire-and-forget, silencioso ante fallo.
      import("../lib/uxTelemetry").then(({ logUXEvent }) => {
        logUXEvent("onboarding_ui_mode_selected", {
          choice: formData.ui_mode || "simple",
        });
      }).catch(() => {});

      // Extract ui_mode from the restaurant payload (belongs to user prefs, not restaurant)
      const { ui_mode: _uiMode, ...restaurantPayload } = formData;

      // Create restaurant
      const restaurant = await api.createRestaurant(restaurantPayload);

      // Persist the new restaurant as active so the RestaurantProvider
      // (mounted inside DashboardLayout) switches to it on mount instead of
      // keeping the previously-selected one.
      localStorage.setItem("currentRestaurantId", restaurant.restaurant_id);

      // Trigger supplier auto-seed WITH coordinates (overrides the
      // fire-and-forget one kicked off by the backend without geo info).
      // Runs in parallel — don't block the user flow on this.
      if (geoCoords) {
        api.autoSeedSuppliers(restaurant.restaurant_id, geoCoords)
          .then(res => {
            if (res?.created > 0) {
              toast.success(`${res.created} proveedores recomendados creados`, {
                description: `Revisa tu lista en la sección Proveedores.`,
                duration: 6000,
              });
            }
          })
          .catch(() => { /* silent — backend already kicked off a fallback */ });
      }
      
      // If we have an AI generated menu, create it
      if (aiGeneratedMenu && aiGeneratedMenu.categories) {
        const menu = await api.createMenu(restaurant.restaurant_id, {
          name: aiGeneratedMenu.menu_name || "Carta Principal",
          description: "Menu generado con IA",
          is_active: true,
          menu_type: "regular"
        });

        // Create all categories in parallel
        const categoryResults = await Promise.all(
          aiGeneratedMenu.categories.map((category, index) =>
            api.createCategory(menu.menu_id, {
              name: category.name,
              display_order: index,
            })
          )
        );

        // Collect all dishes across categories for a single bulk insert
        const bulkDishes = [];
        aiGeneratedMenu.categories.forEach((category, index) => {
          const cat = categoryResults[index];
          (category.dishes || []).forEach((dish) => {
            bulkDishes.push({
              name: dish.name,
              description: dish.description || "",
              price: dish.suggested_price || dish.price || 0,
              category_id: cat.category_id,
              is_active: true,
            });
          });
        });

        if (bulkDishes.length > 0) {
          await api.createDishesBulk(menu.menu_id, bulkDishes);
        }
      }

      // If we have imported menu data, confirm the import with the new restaurant
      if (importedMenuData) {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        await fetch(`${API_URL}/api/ai/confirm-menu-import`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            import_id: importedMenuData.import_id,
            restaurant_id: restaurant.restaurant_id,
            menu_name: importedMenuData.menu_name || formData.name,
            items: importedMenuData.items,
          }),
        });
      }

      toast.success("¡Tu restaurante esta listo!");
      navigate("/app", { state: { welcomeSuppliers: true, restaurantId: restaurant.restaurant_id } });
    } catch (error) {
      // Plan-limit 403 toast is now shown automatically by api.js with a
      // "Mejorar plan" CTA. We just skip the duplicate generic toast here.
      if (error?.isPlanLimit) return;
      toast.error(error?.message || "Error al crear el restaurante");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-3xl mx-auto py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-4">
            <ChefHat className="w-8 h-8 text-primary-foreground" />
          </div>
          <h1 className="font-heading text-3xl font-bold mb-2">Configura tu negocio</h1>
          <p className="text-muted-foreground">En pocos pasos tendrás todo listo</p>
        </div>

        {/* Progress */}
        <div className="flex items-center justify-center gap-2 mb-12">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <div 
                className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors ${
                  currentStep >= step.id 
                    ? "bg-primary text-primary-foreground" 
                    : "bg-muted text-muted-foreground"
                }`}
              >
                {currentStep > step.id ? <Check className="w-5 h-5" /> : step.id}
              </div>
              {index < steps.length - 1 && (
                <div className={`w-16 h-1 mx-2 rounded ${currentStep > step.id ? "bg-primary" : "bg-muted"}`} />
              )}
            </div>
          ))}
        </div>

        {/* Step 1: Business Type */}
        {currentStep === 1 && (
          <Card className="border-none shadow-lg" data-testid="onboarding-step-1">
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <Building2 className="w-6 h-6 text-primary" />
                Tipo de negocio
              </CardTitle>
              <CardDescription>Cuéntanos qué tipo de establecimiento tienes</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tipo de negocio *</Label>
                  <Select 
                    value={formData.business_type} 
                    onValueChange={(v) => setFormData({ ...formData, business_type: v })}
                  >
                    <SelectTrigger data-testid="business-type-select">
                      <SelectValue placeholder="Selecciona..." />
                    </SelectTrigger>
                    <SelectContent>
                      {businessTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Tipo de cocina *</Label>
                  {(() => {
                    const knownVals = cuisineTypes.map(t => t.value).filter(v => v !== 'other');
                    const isPreset = knownVals.includes(formData.cuisine_type);
                    const selectVal = isPreset ? formData.cuisine_type : 'other';
                    const showInput = !isPreset;
                    const inputVal = formData.cuisine_type === 'other' ? '' : (isPreset ? '' : formData.cuisine_type);
                    return (
                      <>
                        <Select
                          value={selectVal}
                          onValueChange={(v) => setFormData({ ...formData, cuisine_type: v })}
                        >
                          <SelectTrigger data-testid="cuisine-type-select">
                            <SelectValue placeholder="Selecciona..." />
                          </SelectTrigger>
                          <SelectContent>
                            {cuisineTypes.map((type) => (
                              <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {showInput && (
                          <Input
                            placeholder="Especifica el tipo de cocina..."
                            value={inputVal}
                            onChange={(e) => setFormData({ ...formData, cuisine_type: e.target.value || 'other' })}
                            data-testid="cuisine-type-custom-input"
                            autoFocus
                          />
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Rango de precios</Label>
                <Select 
                  value={formData.price_range} 
                  onValueChange={(v) => setFormData({ ...formData, price_range: v })}
                >
                  <SelectTrigger data-testid="price-range-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {priceRanges.map((range) => (
                      <SelectItem key={range.value} value={range.value}>{range.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Público objetivo (opcional)</Label>
                <Input
                  placeholder="Ej: Familias, jóvenes, turistas..."
                  value={formData.target_audience}
                  onChange={(e) => setFormData({ ...formData, target_audience: e.target.value })}
                  data-testid="target-audience-input"
                />
              </div>

              <div className="flex justify-end">
                <Button 
                  onClick={() => setCurrentStep(2)}
                  disabled={!formData.business_type || !formData.cuisine_type}
                  className="rounded-full px-8"
                  data-testid="step-1-next-btn"
                >
                  Siguiente
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Import Method */}
        {currentStep === 2 && (
          <Card className="border-none shadow-lg" data-testid="onboarding-step-2">
            <CardHeader>
              <CardTitle>¿Cómo quieres empezar?</CardTitle>
              <CardDescription>Elige la opción que mejor se adapte a ti</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {!importMethod ? (
                <div className="grid sm:grid-cols-3 gap-4">
                  <Card 
                    className="cursor-pointer hover:border-primary transition-colors"
                    onClick={() => setImportMethod("import")}
                    data-testid="import-option"
                  >
                    <CardContent className="p-6 text-center">
                      <Upload className="w-10 h-10 mx-auto mb-4 text-primary" />
                      <h3 className="font-semibold mb-2">Importar menú</h3>
                      <p className="text-sm text-muted-foreground">Sube una foto o PDF de tu menú actual</p>
                    </CardContent>
                  </Card>

                  <Card 
                    className="cursor-pointer hover:border-primary transition-colors"
                    onClick={() => setImportMethod("ai")}
                    data-testid="ai-option"
                  >
                    <CardContent className="p-6 text-center">
                      <Sparkles className="w-10 h-10 mx-auto mb-4 text-primary" />
                      <h3 className="font-semibold mb-2">Generar con IA</h3>
                      <p className="text-sm text-muted-foreground">La IA crea un menú profesional para ti</p>
                    </CardContent>
                  </Card>

                  <Card 
                    className="cursor-pointer hover:border-primary transition-colors"
                    onClick={() => setCurrentStep(3)}
                    data-testid="manual-option"
                  >
                    <CardContent className="p-6 text-center">
                      <ImageIcon className="w-10 h-10 mx-auto mb-4 text-primary" />
                      <h3 className="font-semibold mb-2">Crear manual</h3>
                      <p className="text-sm text-muted-foreground">Añade platos uno a uno después</p>
                    </CardContent>
                  </Card>
                </div>
              ) : importMethod === "import" ? (
                <div className="space-y-6 text-center py-8">
                  <Upload className="w-12 h-12 mx-auto text-primary" />
                  <h3 className="font-semibold text-lg">Importar menu desde archivo</h3>
                  <p className="text-sm text-muted-foreground max-w-md mx-auto">
                    Sube un PDF o imagen de tu menu. La IA leera todas las paginas, detectara platos y bebidas automaticamente.
                  </p>
                  <div className="flex gap-3 justify-center">
                    <Button variant="outline" onClick={() => setImportMethod(null)}>
                      <ArrowLeft className="w-4 h-4 mr-2" /> Volver
                    </Button>
                    <Button onClick={openImportDialog} data-testid="open-import-dialog-btn">
                      <Upload className="w-4 h-4 mr-2" /> Seleccionar archivo
                    </Button>
                  </div>

                  <MenuImportDialog
                    open={importDialogOpen}
                    onClose={() => setImportDialogOpen(false)}
                    restaurantId={null}
                    onConfirmPreview={(data) => {
                      setImportedMenuData(data);
                      setImportDialogOpen(false);
                      setCurrentStep(3);
                      toast.success(`Menu analizado: ${data.total_food} platos + ${data.total_beverages} bebidas`);
                    }}
                  />
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="bg-muted/50 rounded-xl p-6">
                    <h3 className="font-semibold mb-2 flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-primary" />
                      Generación con IA
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Basándome en tu tipo de negocio ({formData.business_type}) y cocina ({formData.cuisine_type}), 
                      generaré un menú profesional con categorías, platos y precios sugeridos.
                    </p>
                    <ul className="text-sm space-y-2 text-muted-foreground">
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-500" />
                        Categorías organizadas (entrantes, principales, postres...)
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-500" />
                        Nombres y descripciones atractivas
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-500" />
                        Precios sugeridos según tu rango
                      </li>
                    </ul>
                  </div>

                  <div className="flex gap-4 justify-between">
                    <Button variant="outline" onClick={() => setImportMethod(null)}>
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Volver
                    </Button>
                    <Button 
                      onClick={handleGenerateMenu}
                      disabled={aiLoading}
                      className="rounded-full px-8"
                    >
                      {aiLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Generando...
                        </>
                      ) : (
                        <>
                          Generar menú
                          <Sparkles className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {!importMethod && (
                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setCurrentStep(1)}>
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Anterior
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 3: Final Details */}
        {currentStep === 3 && (
          <Card className="border-none shadow-lg" data-testid="onboarding-step-3">
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <Check className="w-6 h-6 text-primary" />
                Últimos detalles
              </CardTitle>
              <CardDescription>Completa la información de tu negocio</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {aiGeneratedMenu && (
                <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 mb-6">
                  <p className="text-sm font-medium text-green-700 dark:text-green-300 flex items-center gap-2">
                    <Check className="w-5 h-5" />
                    Menu generado: {aiGeneratedMenu.categories?.length || 0} categorias, 
                    {aiGeneratedMenu.categories?.reduce((acc, c) => acc + (c.dishes?.length || 0), 0) || 0} platos
                  </p>
                </div>
              )}

              {importedMenuData && (
                <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 mb-6">
                  <p className="text-sm font-medium text-green-700 dark:text-green-300 flex items-center gap-2">
                    <Check className="w-5 h-5" />
                    Menu importado: {importedMenuData.total_food} platos, {importedMenuData.total_beverages} bebidas
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label>Nombre de tu negocio *</Label>
                <Input
                  placeholder="Ej: La Tasca de Juan"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="business-name-input"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <Label>Dirección</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleUseMyLocation}
                      disabled={locating}
                      className="h-6 px-2 text-[11px] text-violet-600 hover:bg-violet-50 gap-1"
                      data-testid="onboarding-use-location-btn"
                    >
                      {locating ? <Loader2 className="w-3 h-3 animate-spin" /> : <MapPin className="w-3 h-3" />}
                      {locating ? "Detectando…" : "Usar mi ubicación"}
                    </Button>
                  </div>
                  <Input
                    placeholder="Calle, número, ciudad"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Teléfono</Label>
                  <Input
                    placeholder="+34 600 000 000"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Descripción (opcional)</Label>
                <Textarea
                  placeholder="Describe tu negocio en unas líneas..."
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>

              {/* Fase 7 · UI Mode selector — decide cómo se le mostrará la app al usuario */}
              <div className="space-y-3 pt-2 border-t">
                <div>
                  <Label className="text-sm font-medium">¿Cómo quieres ver la app?</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Podrás cambiarlo cuando quieras desde la barra superior.
                  </p>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, ui_mode: "simple" })}
                    data-testid="onboarding-ui-mode-simple"
                    className={`text-left p-4 rounded-xl border-2 transition-all ${
                      formData.ui_mode === "simple"
                        ? "border-primary bg-primary/5 shadow-sm"
                        : "border-muted hover:border-primary/50"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Sparkles className="w-4 h-4 text-primary" />
                      <span className="font-semibold text-sm">Modo Simple</span>
                      <span className="text-[10px] uppercase tracking-wider text-primary/80 bg-primary/10 px-1.5 py-0.5 rounded-full">
                        Recomendado
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-snug">
                      Solo lo esencial a la vista. Las opciones avanzadas están ocultas hasta que las necesites.
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, ui_mode: "advanced" })}
                    data-testid="onboarding-ui-mode-advanced"
                    className={`text-left p-4 rounded-xl border-2 transition-all ${
                      formData.ui_mode === "advanced"
                        ? "border-primary bg-primary/5 shadow-sm"
                        : "border-muted hover:border-primary/50"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-sm">Modo Avanzado</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-snug">
                      Todos los campos y opciones visibles siempre. Ideal si ya dominas restaurantes y sistemas.
                    </p>
                  </button>
                </div>
              </div>

              <div className="flex gap-4 justify-between pt-4">
                <Button variant="outline" onClick={() => setCurrentStep(2)}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Anterior
                </Button>
                <Button 
                  onClick={handleFinish}
                  disabled={loading || !formData.name}
                  className="rounded-full px-8"
                  data-testid="finish-onboarding-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creando...
                    </>
                  ) : (
                    <>
                      Finalizar
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
